// Клієнт API контингенту (U04, /api/contingent/*) + хуки TanStack Query. Типи — дзеркало відповідей модуля API.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { SubgroupNaming } from '@unisched/shared';
import { api, type Paged, type QueryParams } from '@/lib/api';

const enc = encodeURIComponent;

export interface RefView {
  id: string;
  code: string;
  name: string;
}

export interface GroupSubgroup {
  id: string;
  code: string;
  name: string;
  studentsCount: number;
  criterion: string | null;
}

export interface GroupRow {
  id: string;
  code: string;
  name: string;
  academicYear: RefView;
  program: RefView;
  faculty: RefView | null;
  facultyOverride: boolean;
  course: number;
  studentsCount: number;
  language: { id: string; code: string; label: string };
  curriculum: { id: string; code: string } | null;
  intakeYear: number | null;
  isActive: boolean;
  isDemo: boolean;
  subgroups: GroupSubgroup[];
  streamsCount: number;
}

export interface GroupCard extends GroupRow {
  curatorName: string | null;
  baseLocation: RefView | null;
  streams: Array<{ id: string; code: string; name: string; kind: { code: string; label: string }; groupsCount: number }>;
  studentsRecords: number;
  /** навантаження групи: на групу + на її підгрупи + на потоки групи */
  assignmentsCount: number;
  assignmentsByAudience: { group: number; subgroups: number; streams: number };
  divisions: Array<{ criterion: string | null; subgroupIds: string[]; total: number }>;
  warnings: string[];
  can: { edit: boolean; readStudents: boolean };
}

export interface StreamGroupView {
  id: string;
  code: string;
  name: string;
  course: number;
  studentsCount: number;
  language: { id: string; code: string; label: string };
  program: { id: string; code: string; name: string };
  facultyId: string | null;
}

export interface StreamCard {
  id: string;
  code: string;
  name: string;
  academicYear: { id: string; code: string };
  kind: { id: string; code: string; label: string };
  program: { id: string; code: string; name: string } | null;
  faculty: RefView | null;
  course: number | null;
  studentsCountOverride: number | null;
  size: number;
  groupsTotal: number;
  groups: StreamGroupView[];
  warnings: Array<{ code: string; message: string }>;
  isActive: boolean;
  isDemo: boolean;
  can: { edit: boolean };
}

export interface CurrentSemesterView {
  semester: { id: string; code: string; number: number; name: string | null; startDate: string; endDate: string; weeks: number };
  academicYear: { id: string; code: string; name: string | null };
}

export interface BulkCreateInput {
  academicYearId: string;
  programId: string;
  course: number;
  count: number;
  studentsCount: number;
  languageId: string;
  namePattern: string;
  startNo?: number;
  intakeYear?: number;
  facultyId?: string | null;
  dryRun: boolean;
}

export interface BulkCreateResult {
  dryRun: boolean;
  created: number;
  groups: Array<{ id: string | null; code: string; name: string; intakeYear: number; curriculumId: string | null }>;
  curriculum: { id: string; code: string; version: number } | null;
  facultyId: string | null;
}

export interface SplitInput {
  parts?: number;
  maxSize?: number;
  naming: SubgroupNaming;
  criterion?: string | null;
}

export interface SplitResult {
  groupId: string;
  sizes: number[];
  criterion: string | null;
  created: GroupSubgroup[];
  removed: number;
  subgroups: GroupSubgroup[];
}

export interface AutoStreamsInput {
  academicYearId: string;
  programId: string;
  course: number;
  languageId?: string | null;
  /** факультет груп (TZ-DELTAS п.4); порожньо — усі факультети програми, окремі потоки */
  facultyId?: string | null;
  maxSize?: number;
  dryRun: boolean;
}

export interface AutoStreamsResult {
  dryRun: boolean;
  created: number;
  streams: Array<{ id: string | null; code: string; name: string; languageId: string; facultyId: string; groupIds: string[]; groupNames: string[]; size: number }>;
  skipped: Array<{ id: string; name: string; reason: string }>;
}

export interface StudentRow {
  id: string;
  code: string;
  fullName: string;
  email: string | null;
  status: string;
  isDemo: boolean;
  subgroups: Array<{ id: string; name: string }>;
  streams: Array<{ id: string; code: string; name: string }>;
}

export const contingentApi = {
  /**
   * Поточний семестр сервера або null (семестрів немає). Порожнє тіло (старі версії API) теж дає null: TanStack Query
   * не приймає undefined як дані запиту («Query data cannot be undefined» у консолі й стан помилки).
   */
  currentSemester: async (): Promise<CurrentSemesterView | null> => (await api.get<CurrentSemesterView | null | undefined>('/contingent/semesters/current')) ?? null,
  groups: (params: QueryParams, signal?: AbortSignal) => api.get<Paged<GroupRow>>('/contingent/groups', params, { signal }),
  group: (id: string) => api.get<GroupCard>(`/contingent/groups/${enc(id)}`),
  groupStudents: (id: string, params: QueryParams) => api.get<Paged<StudentRow>>(`/contingent/groups/${enc(id)}/students`, params),
  bulkCreate: (input: BulkCreateInput) => api.post<BulkCreateResult>('/contingent/groups/bulk-create', input),
  split: (id: string, input: SplitInput) => api.post<SplitResult>(`/contingent/groups/${enc(id)}/split`, input),
  pickCurriculum: (id: string) =>
    api.post<{ curriculumId: string | null; curriculumCode: string | null; reason: string; changed: boolean }>(`/contingent/groups/${enc(id)}/pick-curriculum`),
  setFaculty: (id: string, facultyId: string | null) => api.put<GroupCard>(`/contingent/groups/${enc(id)}/faculty`, { facultyId }),
  stream: (id: string) => api.get<StreamCard>(`/contingent/streams/${enc(id)}`),
  setStreamGroups: (id: string, groupIds: string[]) => api.put<StreamCard>(`/contingent/streams/${enc(id)}/groups`, { groupIds }),
  autoStreams: (input: AutoStreamsInput) => api.post<AutoStreamsResult>('/contingent/streams/auto', input),
};

export const contingentKeys = {
  all: ['contingent'] as const,
  current: ['contingent', 'semesters', 'current'] as const,
  groups: (params: QueryParams) => ['contingent', 'groups', params] as const,
  group: (id: string) => ['contingent', 'group', id] as const,
  students: (id: string, params: QueryParams) => ['contingent', 'group', id, 'students', params] as const,
  stream: (id: string) => ['contingent', 'stream', id] as const,
};

/** Поточний семестр сервера: data — CurrentSemesterView або null (семестрів немає; не помилка). */
export function useCurrentSemester() {
  return useQuery({ queryKey: contingentKeys.current, queryFn: () => contingentApi.currentSemester(), staleTime: 5 * 60_000, retry: false });
}

export function useGroups(params: QueryParams, enabled = true) {
  return useQuery({
    queryKey: contingentKeys.groups(params),
    queryFn: ({ signal }) => contingentApi.groups(params, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useGroupCard(id: string | undefined) {
  return useQuery({ queryKey: contingentKeys.group(id ?? ''), queryFn: () => contingentApi.group(id!), enabled: !!id, retry: false });
}

export function useGroupStudents(id: string, params: QueryParams, enabled: boolean) {
  return useQuery({
    queryKey: contingentKeys.students(id, params),
    queryFn: () => contingentApi.groupStudents(id, params),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useStreamCard(id: string | undefined) {
  return useQuery({ queryKey: contingentKeys.stream(id ?? ''), queryFn: () => contingentApi.stream(id!), enabled: !!id, retry: false });
}

/** Інвалідація контингенту й довідників груп/підгруп/потоків/студентів (після змін). */
export function useInvalidateContingent() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: contingentKeys.all });
    for (const code of ['groups', 'subgroups', 'streams', 'students', 'academic_years']) void qc.invalidateQueries({ queryKey: ['directories', code] });
    void qc.invalidateQueries({ queryKey: ['directories', 'summary'] });
  }, [qc]);
}

export function useSplitGroup(id: string) {
  const invalidate = useInvalidateContingent();
  return useMutation({
    mutationFn: (input: SplitInput) => contingentApi.split(id, input),
    onSuccess: () => invalidate(),
    meta: { silent: true },
  });
}

export function usePickCurriculum(id: string) {
  const invalidate = useInvalidateContingent();
  return useMutation({ mutationFn: () => contingentApi.pickCurriculum(id), onSuccess: () => invalidate(), meta: { errorMessage: 'Не вдалося підібрати план' } });
}

export function useSetGroupFaculty(id: string) {
  const invalidate = useInvalidateContingent();
  return useMutation({
    mutationFn: (facultyId: string | null) => contingentApi.setFaculty(id, facultyId),
    onSuccess: () => invalidate(),
    meta: { errorMessage: 'Не вдалося змінити факультет групи', successMessage: 'Факультет групи збережено' },
  });
}

export function useBulkCreateGroups() {
  const invalidate = useInvalidateContingent();
  return useMutation({
    mutationFn: (input: BulkCreateInput) => contingentApi.bulkCreate(input),
    onSuccess: (r) => {
      if (!r.dryRun) invalidate();
    },
    meta: { silent: true },
  });
}

export function useSetStreamGroups(id: string) {
  const invalidate = useInvalidateContingent();
  return useMutation({
    mutationFn: (groupIds: string[]) => contingentApi.setStreamGroups(id, groupIds),
    onSuccess: () => invalidate(),
    meta: { errorMessage: 'Не вдалося зберегти склад потоку', successMessage: 'Склад потоку збережено' },
  });
}

export function useAutoStreams() {
  const invalidate = useInvalidateContingent();
  return useMutation({
    mutationFn: (input: AutoStreamsInput) => contingentApi.autoStreams(input),
    onSuccess: (r) => {
      if (!r.dryRun) invalidate();
    },
    meta: { silent: true },
  });
}
